import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useClipboard } from 'use-clipboard-copy'
import { Button } from 'components/atoms/button'

interface ShareLinkCardProps {
	/** The link to share; copied in full, shown without its protocol */
	url: string
	className?: string
}

const COPIED_TIMEOUT_MS = 2000

/** The attendee's unique link with a one-tap Copy: a header, the link on a recessed row, and a clear button. */
function ShareLinkCard({ url, className }: Readonly<ShareLinkCardProps>) {
	const clipboard = useClipboard({ copiedTimeout: COPIED_TIMEOUT_MS })
	const displayUrl = url.replace(/^https?:\/\//, '')

	return (
		<section
			aria-label="Share your unique link"
			className={`w-full rounded-2xl border border-solid border-white/10 bg-white/[0.04] p-4 text-left ${className ?? ''}`}
		>
			<div className="flex items-center gap-3">
				<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-surface/20 text-accent-surface">
					<FontAwesomeIcon icon={['fas', 'link']} aria-hidden="true" />
				</span>
				<div className="min-w-0">
					<h4 className="text-sm font-semibold leading-tight text-primary-text">Share your unique link</h4>
					<p className="text-xs leading-snug text-secondary-text">Send it to friends so they can join you.</p>
				</div>
			</div>
			<div className="mt-3 flex items-center gap-2 rounded-xl bg-black/30 py-1.5 pl-3 pr-1.5">
				<span className="min-w-0 flex-1 truncate font-mono text-xs text-secondary-text" title={url}>
					{displayUrl}
				</span>
				<Button
					variant="primary"
					className="h-9 min-w-[88px] shrink-0 gap-1.5 px-4 text-sm"
					onClick={() => clipboard.copy(url)}
					aria-label={clipboard.copied ? 'Link copied' : 'Copy link'}
				>
					<FontAwesomeIcon icon={['fas', clipboard.copied ? 'check' : 'copy']} aria-hidden="true" />
					<span aria-live="polite">{clipboard.copied ? 'Copied' : 'Copy'}</span>
				</Button>
			</div>
		</section>
	)
}

export { ShareLinkCard }
export type { ShareLinkCardProps }
